import type { ReactNode } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { plannedEntry, roadmapLink, stageOf, type PlannedId } from './plannedRegistry';

/** «Previst: <the stage of the roadmap>», what every chip says on hover. */
function expected(id: PlannedId): string {
  return ca.planned.expected(ca.planned.stages[stageOf(plannedEntry(id).step)]);
}

/** A signed-in reader can open the roadmap; a visitor cannot, and a page drawn outside a router has no links. */
function useRoadmapLinks(): boolean {
  const { me } = useAuth();
  return useInRouterContext() && me.authenticated;
}

const reasonText = (why: 'panel' | 'ingame' | undefined) =>
  why === 'ingame' ? ca.planned.waitsGame : why === 'panel' ? ca.planned.waitsPanel : null;

/**
 * Marks what the design asks for and the code does not do yet, so a visitor sees the shape of
 * the finished product and knows what is pending. Every line is an entry of the registry
 * (`plannedRegistry.ts`), which ties it to its roadmap step.
 *
 * `items` and `why` are the free-text form this component had before the registry: only the
 * admin page still uses it, and `plannedRegistry.test.ts` names that one file.
 */
export function Planned({
  title = ca.planned.title,
  ids,
  items,
  why,
  children,
}: {
  title?: string;
  ids?: readonly PlannedId[];
  /** @deprecated free text; name registry entries with `ids`. */
  items?: readonly string[];
  /** @deprecated the registry's entries carry their own reason. */
  why?: 'game' | 'panel';
  children?: ReactNode;
}) {
  const links = useRoadmapLinks();
  const reasons = new Set((ids ?? []).map((id) => plannedEntry(id).why));
  // The card says why it waits when every line waits for the same thing.
  const shared = ids && reasons.size === 1 ? [...reasons][0] : undefined;
  const reason = reasonText(shared ?? (why === 'game' ? 'ingame' : why));
  const stages = new Set((ids ?? []).map((id) => expected(id)));
  return (
    <aside className="card planned" aria-label={ca.planned.chip}>
      <div className="row">
        <h2>{title}</h2>
        <span className="chip planned" title={stages.size === 1 ? [...stages][0] : undefined}>
          {ca.planned.chip}
        </span>
        {reason && <span className="chip">{reason}</span>}
      </div>
      {children}
      {ids && (
        <ul className="plain">
          {ids.map((id) => (
            <li key={id} title={expected(id)} data-planned-id={id}>
              {links ? (
                <Link to={roadmapLink(id)}>{plannedEntry(id).label}</Link>
              ) : (
                plannedEntry(id).label
              )}
            </li>
          ))}
        </ul>
      )}
      {items && (
        <ul className="plain">
          {items.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      )}
    </aside>
  );
}

/**
 * A small «Pendent» tag beside a control or a heading. `named` prints what is pending instead
 * of the word, where nothing beside the chip says it.
 */
export function PlannedChip({
  id,
  named = false,
  inline = false,
}: {
  id: PlannedId;
  named?: boolean;
  /** Inside a button or a tab, where a link cannot be: the chip is plain text. */
  inline?: boolean;
}) {
  const roadmapLinks = useRoadmapLinks();
  const links = roadmapLinks && !inline;
  const entry = plannedEntry(id);
  const text = named ? (entry.chip ?? entry.label) : ca.planned.chip;
  return links ? (
    <Link className="chip planned" to={roadmapLink(id)} title={expected(id)} data-planned-id={id}>
      {text}
    </Link>
  ) : (
    <span className="chip planned" title={expected(id)} data-planned-id={id}>
      {text}
    </span>
  );
}

/**
 * A button the design asks for whose action does not exist yet: visible, disabled, explained.
 * Its chip is no link, since nothing inside a disabled button can be followed.
 */
export function PlannedButton({ id, children }: { id?: PlannedId; children: ReactNode }) {
  return (
    <button
      type="button"
      className="planned"
      disabled
      title={id ? `${ca.planned.soon} · ${expected(id)}` : ca.planned.soon}
      data-planned-id={id}
    >
      {children} <span className="chip planned">{ca.planned.chip}</span>
    </button>
  );
}
